/**
 * useFleetStream · v2.6.9 batch BI
 * =================================
 * React hook that opens a WebSocket to the fleet streaming gateway based on
 * the caller's role:
 *
 *   role = "driver"   → /api/ws/driver   · PUSHES geolocation every 3 s
 *   role = "dispatch" → /api/ws/dispatch · RECEIVES every driver's pings + signal-lost events
 *   role = "rider"    → /api/ws/rider    · RECEIVES the assigned driver's pings (gated by trip state)
 *
 * Driver mode is the only one that actually writes to the socket — it also:
 *   · uses navigator.geolocation.watchPosition for real-time GPS
 *   · throttles pushes with a delta-update gate (≥3 s OR ≥10 m of movement)
 *   · holds a Screen Wake Lock (where supported) so the screen doesn't sleep
 *   · auto-reconnects with exponential backoff
 *
 * Dispatch + rider modes are read-only consumers; messages are appended to
 * the returned `events` array (which the caller can read via React state).
 */
import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND_HTTP = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_WS = BACKEND_HTTP.replace(/^http/, "ws");

// v2.7.65+E87 · heartbeat tuning per SYSTEM CRITICAL spec.
// On-trip stays at 3s (already tight — one ping every bus-stop's radius).
// Idle bumped from 15s → 8s so dispatch sees driver presence + drift
// within the same 10-second window as the on-trip cadence.
const HEARTBEAT_MS_ACTIVE = 3000;     // 3s when on an active trip (real-time tracking)
const HEARTBEAT_MS_IDLE = 8000;       // v2.7.65+E87 · 8s when idle (was 15s)
const MIN_DISTANCE_M = 10;            // delta-update gate: 10 m of movement
const RECONNECT_BASE_MS = 1500;       // exponential backoff base
const RECONNECT_MAX_MS = 30000;
// v2.7.0 batch DL · Slow-driver thresholds. End-to-end ping latency above
// SLOW_THRESHOLD_MS sustained for SLOW_SUSTAIN_MS flags a driver as "slow"
// (cell-edge / backgrounded tab / overheating phone). Below SLOW_RECOVER_MS
// for one ping clears the flag immediately.
const SLOW_THRESHOLD_MS = 5000;
const SLOW_SUSTAIN_MS = 30000;
const SLOW_RECOVER_MS = 2500;

function haversineMeters(a, b) {
  if (!a || !b) return Infinity;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * @param {object} opts
 * @param {"driver"|"dispatch"|"rider"} opts.role
 * @param {string|null}  opts.token        — JWT (driver, supervisor, or customer)
 * @param {boolean}      opts.active       — open the socket only when true (e.g. driver on-duty, rider on active trip)
 * @param {object|null}  opts.tripContext  — optional {trip_id, origin, destination, current_fare} echoed in driver pings
 */
export default function useFleetStream({ role, token, active, tripContext = null }) {
  const [status, setStatus] = useState("idle"); // "idle" | "connecting" | "open" | "closed" | "error"
  const [events, setEvents] = useState([]);      // last 200 dispatch/rider messages
  const [drivers, setDrivers] = useState({});    // dispatch: driver_id -> last ping
  const [lostDrivers, setLostDrivers] = useState(new Set()); // dispatch: signal-lost driver_ids
  const [criticalDrivers, setCriticalDrivers] = useState(new Set()); // dispatch: signal_critical (>60s silent)
  // v2.6.9 batch BM · drivers whose WS gracefully disconnected (closed app,
  // signed off, lost signal). Distinct from `lostDrivers` so the map can
  // render them as offline-grey without firing an alarm.
  const [offlineDrivers, setOfflineDrivers] = useState(new Set());
  const [dispatchedRecently, setDispatchedRecently] = useState(new Set()); // dispatch: drivers that just got a new trip (1.5s flash)
  // v2.7.0 batch DK · End-to-end ping latency tracking (dispatch-side only).
  // We exponentially-smooth the (Date.now() - parsed.t_emit) value so the
  // status bar can show a stable "ping latency" number rather than a jittery
  // per-frame readout.
  const [pingLatencyMs, setPingLatencyMs] = useState(null);
  // v2.7.0 batch DL · Per-driver smoothed latency + "slow driver" detection.
  // We track:
  //   • driverLatencyMs[id]    → EMA of (now - t_emit) for that driver
  //   • slowSinceRef[id]       → ms epoch of first observation > SLOW_THRESHOLD
  //   • slowDrivers (Set)      → driver_ids that have been slow for ≥ SLOW_SUSTAIN_MS
  // The LiveDispatchMap reads `slowDrivers` to render an amber pulse + the
  // dispatch console plays a soft chime + posts one toast per transition.
  const [driverLatencyMs, setDriverLatencyMs] = useState({}); // id -> ms (smoothed)
  const [slowDrivers, setSlowDrivers] = useState(new Set());  // sustained slow >30s
  // v2.7.65+E86 · dedupe forced ping-request frames (5s window)
  const lastPingRequestRef = useRef(0);
  const slowSinceRef = useRef({});                            // id -> first-bad ms epoch
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const wakeLockRef = useRef(null);
  const geoWatchIdRef = useRef(null);
  const lastPushRef = useRef({ ts: 0, lat: null, lng: null });
  const closedByUsRef = useRef(false);
  const tripContextRef = useRef(tripContext);
  useEffect(() => { tripContextRef.current = tripContext; }, [tripContext]);

  // v2.6.9 batch BT · Cross-route trip-context bridge. Pages that aren't the
  // Driver Dashboard (e.g. MeterPage) can broadcast their active-meter state
  // via `window.dispatchEvent(new CustomEvent("rtg:meter_state", {detail:{
  // on_trip, passengers, trip_id, current_fare, origin, destination}}))`.
  // The hook merges that into every outgoing frame so dispatch sees the
  // passenger count badge while a meter is running.
  const meterStateRef = useRef(null);
  useEffect(() => {
    if (role !== "driver") return undefined;
    const onMeterState = (e) => { meterStateRef.current = e.detail || null; };
    window.addEventListener("rtg:meter_state", onMeterState);
    return () => window.removeEventListener("rtg:meter_state", onMeterState);
  }, [role]);

  const pushDriverFrame = useCallback((pos) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    const ctx = tripContextRef.current || {};
    const meter = meterStateRef.current || {};
    const onTrip = !!(meter.on_trip || ctx.trip_id);
    const frame = {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      speed: pos.coords.speed != null ? pos.coords.speed * 2.237 : null,  // m/s → mph
      heading: pos.coords.heading || null,
      on_trip: onTrip,
      trip_id: meter.trip_id || ctx.trip_id || null,
      origin: meter.origin || ctx.origin || null,
      destination: meter.destination || ctx.destination || null,
      current_fare: meter.current_fare ?? ctx.current_fare ?? null,
      // v2.6.9 batch BT · passenger count for live dispatch overlay badge.
      passengers: onTrip ? (meter.passengers ?? ctx.passengers ?? null) : null,
      // v2.7.0 batch DK · client-side emit timestamp (ms epoch) so the dispatch
      // map can compute true end-to-end ping latency (client → server → broker
      // → dispatch socket). Subtracted from receipt-time on the dispatch
      // hook below.
      t_emit: Date.now(),
    };
    try {
      wsRef.current.send(JSON.stringify(frame));
      lastPushRef.current = { ts: Date.now(), lat: frame.lat, lng: frame.lng };
    } catch (e) {
      // socket may have just closed — let the onclose handler reconnect.
      // We log because silent send failures on a SAFETY system are bad
      // (the driver thinks GPS is streaming but dispatch sees nothing).
      console.warn("[useFleetStream] driver ping send failed:", e?.message || e);
    }
  }, []);

  /** v2.7.0 batch DC · adaptive heartbeat — 3s when an active trip is in
   *  progress (real-time GPS), 15s otherwise. Driven entirely by whether
   *  `tripContext.trip_id` is populated by the caller. */
  const isOnTrip = !!(tripContext && tripContext.trip_id);
  const heartbeatMs = isOnTrip ? HEARTBEAT_MS_ACTIVE : HEARTBEAT_MS_IDLE;

  /** Watch geolocation in the BACKGROUND of the driver hook. Pushes a frame
   *  every `heartbeatMs` OR immediately when the device moves >MIN_DISTANCE_M. */
  const startGeoWatching = useCallback(() => {
    if (!("geolocation" in navigator)) return;
    if (geoWatchIdRef.current != null) return;
    geoWatchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        const last = lastPushRef.current;
        const dt = now - last.ts;
        const dist = haversineMeters(
          last.lat != null ? { lat: last.lat, lng: last.lng } : null,
          { lat: pos.coords.latitude, lng: pos.coords.longitude },
        );
        if (dt >= heartbeatMs || dist >= MIN_DISTANCE_M) {
          pushDriverFrame(pos);
        }
      },
      (err) => console.warn("[fleet] geolocation error:", err.message),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 8000 },
    );
  }, [pushDriverFrame, heartbeatMs]);

  const stopGeoWatching = useCallback(() => {
    if (geoWatchIdRef.current != null) {
      navigator.geolocation.clearWatch(geoWatchIdRef.current);
      geoWatchIdRef.current = null;
    }
  }, []);

  /** Heartbeat timer — guarantees a frame every `heartbeatMs` even if the
   *  device is stationary (so the server sees a steady ping pattern).
   *  Auto-rebinds when the driver transitions on/off-trip. */
  useEffect(() => {
    if (role !== "driver" || status !== "open") return undefined;
    const id = setInterval(() => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        pushDriverFrame,
        () => {},
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 6000 },
      );
    }, heartbeatMs);
    return () => clearInterval(id);
  }, [role, status, pushDriverFrame, heartbeatMs]);

  /** Screen wake-lock — keep the device awake while the driver is on duty.
   *  Released automatically when the tab is backgrounded.
   *
   *  v2.7.0 batch DK · Auto-re-acquire when the tab returns to foreground
   *  (the OS releases the lock on background by spec). Also opportunistically
   *  show a persistent Notification when the meter is on-trip — browsers
   *  give visible-notification tabs much higher process priority + delay
   *  freezing, which is the best we can do for "background-resilient meter"
   *  inside a PWA (without wrapping in Capacitor / shipping a native shell). */
  const acquireWakeLock = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    if (wakeLockRef.current && !wakeLockRef.current.released) return;
    try {
      const lock = await navigator.wakeLock.request("screen");
      // Re-attach the release listener so we know if the OS pulled the lock
      // (e.g. low-battery mode on iOS). The visibilitychange listener below
      // will re-acquire when the tab becomes visible again.
      lock.addEventListener?.("release", () => { wakeLockRef.current = null; });
      wakeLockRef.current = lock;
    } catch (e) {
      // wake-lock can be denied (e.g. low battery on iOS); not fatal but
      // worth surfacing so drivers know why their screen still locks.
      console.warn("[useFleetStream] wakeLock denied:", e?.message || e);
    }
  }, []);
  const releaseWakeLock = useCallback(async () => {
    try {
      await wakeLockRef.current?.release();
    } catch (e) {
      // Release errors are non-fatal but log for diagnostics.
      console.warn("[useFleetStream] wakeLock release failed:", e?.message || e);
    }
    wakeLockRef.current = null;
  }, []);

  /** v2.7.0 batch DK · Background-resilience for the PWA driver shell.
   *  When the driver tab visibility flips back to visible we:
   *    1. Re-request the screen wake-lock (always released by the OS on background).
   *    2. Immediately re-establish geolocation watching if it was killed.
   *    3. Force-send a heartbeat frame so dispatch sees the driver alive
   *       within a second of the tab regaining focus (instead of waiting
   *       up to 15s for the next idle heartbeat tick). */
  useEffect(() => {
    if (role !== "driver") return undefined;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      acquireWakeLock();
      if (status === "open" && geoWatchIdRef.current == null) {
        startGeoWatching();
      }
      if (navigator.geolocation && wsRef.current?.readyState === WebSocket.OPEN) {
        navigator.geolocation.getCurrentPosition(
          pushDriverFrame,
          () => {},
          { enableHighAccuracy: true, maximumAge: 0, timeout: 6000 },
        );
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [role, status, acquireWakeLock, pushDriverFrame, startGeoWatching]);

  /** v2.7.0 batch DK · Persistent on-trip Notification. Shows a sticky
   *  notification while a trip meter is running so:
   *    1. The driver visually sees the meter is alive even if they switch
   *       to Google Maps / Waze in the foreground (tap notification → back to app).
   *    2. Android assigns higher process priority to tabs with visible
   *       notifications, dramatically reducing the chance the OS suspends
   *       the meter when memory pressure rises.
   *  Notification quietly no-ops if permission is denied — drivers can
   *  always grant it later from the dashboard banner. */
  const notificationRef = useRef(null);
  useEffect(() => {
    if (role !== "driver") return undefined;
    if (typeof Notification === "undefined") return undefined;
    if (Notification.permission === "default") {
      // Don't auto-prompt — the dashboard's "Enable background meter alerts"
      // banner handles that with rationale. We just react if already granted.
      return undefined;
    }
    if (Notification.permission !== "granted") return undefined;
    const onTrip = !!(tripContext && tripContext.trip_id);
    // Close any prior notification when state changes
    try { notificationRef.current?.close?.(); } catch { /* may have already closed */ }
    notificationRef.current = null;
    if (onTrip) {
      try {
        notificationRef.current = new Notification("Buller Taxi · Meter running", {
          body: `Trip ${tripContext.trip_id} · tap to return to the meter`,
          tag: "buller-meter-active",
          icon: "/logo192.png",
          requireInteraction: true,
          silent: true,
        });
        notificationRef.current.onclick = () => {
          window.focus();
          notificationRef.current?.close?.();
        };
      } catch (e) {
        // Notification may throw on some Safari/iOS versions even when
        // permission says granted — fail soft.
        console.warn("[useFleetStream] notification create failed:", e?.message || e);
      }
    }
    return () => {
      try { notificationRef.current?.close?.(); } catch { /* ignore */ }
      notificationRef.current = null;
    };
  }, [role, tripContext]);

  const connect = useCallback(() => {
    if (!token || !active) return;
    const url = `${BACKEND_WS}/api/ws/${role}?token=${encodeURIComponent(token)}`;
    setStatus("connecting");
    closedByUsRef.current = false;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.warn("[useFleetStream] WebSocket construction failed:", e?.message || e);
      setStatus("error");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("open");
      reconnectAttemptsRef.current = 0;
      if (role === "driver") {
        startGeoWatching();
        acquireWakeLock();
      }
    };

    ws.onmessage = (msg) => {
      let parsed;
      try { parsed = JSON.parse(msg.data); } catch { return; }
      setEvents((prev) => {
        const next = [...prev, parsed];
        return next.length > 200 ? next.slice(-200) : next;
      });
      // dispatch-side: maintain a driver registry for the live map
      if (role === "dispatch") {
        if (parsed.type === "snapshot") {
          const dict = {};
          for (const d of parsed.drivers || []) {
            if (d.driver_id) dict[d.driver_id] = d;
          }
          setDrivers(dict);
          return;
        }
        if (parsed.type === "driver_ping" && parsed.driver_id) {
          // v2.7.0 batch DK · capture end-to-end latency from t_emit field
          // (smoothed via EMA so the status pill doesn't flicker on a
          // single bad packet).
          if (parsed.t_emit) {
            const dt = Math.max(0, Date.now() - parsed.t_emit);
            setPingLatencyMs((prev) => prev == null ? dt : Math.round(prev * 0.7 + dt * 0.3));
            // v2.7.0 batch DL · per-driver smoothed latency + sustained-slow detection
            const did = parsed.driver_id;
            setDriverLatencyMs((prev) => {
              const cur = prev[did];
              const next = cur == null ? dt : Math.round(cur * 0.7 + dt * 0.3);
              return { ...prev, [did]: next };
            });
            const nowMs = Date.now();
            if (dt > SLOW_THRESHOLD_MS) {
              if (!slowSinceRef.current[did]) {
                slowSinceRef.current[did] = nowMs;
              } else if (
                nowMs - slowSinceRef.current[did] >= SLOW_SUSTAIN_MS
                && !slowDrivers.has(did)
              ) {
                setSlowDrivers((prev) => {
                  if (prev.has(did)) return prev;
                  const next = new Set(prev);
                  next.add(did);
                  return next;
                });
              }
            } else if (dt < SLOW_RECOVER_MS) {
              if (slowSinceRef.current[did] != null) {
                delete slowSinceRef.current[did];
                setSlowDrivers((prev) => {
                  if (!prev.has(did)) return prev;
                  const next = new Set(prev);
                  next.delete(did);
                  return next;
                });
              }
            }
          }
          setDrivers((prev) => ({ ...prev, [parsed.driver_id]: parsed }));
          setLostDrivers((prev) => {
            if (!prev.has(parsed.driver_id)) return prev;
            const next = new Set(prev);
            next.delete(parsed.driver_id);
            return next;
          });
          setCriticalDrivers((prev) => {
            if (!prev.has(parsed.driver_id)) return prev;
            const next = new Set(prev);
            next.delete(parsed.driver_id);
            return next;
          });
          // v2.6.9 batch BM · a fresh ping → driver is back online; clear
          // any prior "offline" tag silently.
          setOfflineDrivers((prev) => {
            if (!prev.has(parsed.driver_id)) return prev;
            // v2.7.5 Phase 4 · this driver just transitioned offline → online.
            // Broadcast so the supervisor audio engine plays driver-notification.mp3.
            if (typeof window !== "undefined") {
              try { window.dispatchEvent(new CustomEvent("rtg:driver_online", { detail: parsed })); } catch { /* noop */ }
            }
            const next = new Set(prev);
            next.delete(parsed.driver_id);
            return next;
          });
          return;
        }
        // v2.6.9 batch BM · graceful disconnect — silently mark offline,
        // never fire an alarm. The map flips the marker to slate-grey.
        if (parsed.type === "driver_offline" && parsed.driver_id) {
          setOfflineDrivers((prev) => new Set(prev).add(parsed.driver_id));
          setLostDrivers((prev) => {
            if (!prev.has(parsed.driver_id)) return prev;
            const next = new Set(prev);
            next.delete(parsed.driver_id);
            return next;
          });
          setCriticalDrivers((prev) => {
            if (!prev.has(parsed.driver_id)) return prev;
            const next = new Set(prev);
            next.delete(parsed.driver_id);
            return next;
          });
          // v2.7.0 batch DL · drop slow-flag on graceful offline so the
          // dispatcher doesn't see a stale "slow" marker for a clocked-out driver.
          delete slowSinceRef.current[parsed.driver_id];
          setSlowDrivers((prev) => {
            if (!prev.has(parsed.driver_id)) return prev;
            const next = new Set(prev);
            next.delete(parsed.driver_id);
            return next;
          });
          return;
        }
        if (parsed.type === "signal_lost" && parsed.driver_id) {
          setLostDrivers((prev) => new Set(prev).add(parsed.driver_id));
          return;
        }
        if (parsed.type === "signal_critical" && parsed.driver_id) {
          // Critical drivers are ALSO lost; keep both sets in sync so UI
          // colors + counts remain accurate.
          setLostDrivers((prev) => new Set(prev).add(parsed.driver_id));
          setCriticalDrivers((prev) => new Set(prev).add(parsed.driver_id));
          return;
        }
        if (parsed.type === "signal_recovered" && parsed.driver_id) {
          setLostDrivers((prev) => {
            const next = new Set(prev); next.delete(parsed.driver_id); return next;
          });
          setCriticalDrivers((prev) => {
            const next = new Set(prev); next.delete(parsed.driver_id); return next;
          });
        }
        // v2.6.9 batch BK · panic_alert → dispatch a custom DOM event so any
        // listener (LiveDispatchMap, future global toast) can react. We don't
        // need a Set state here; the alarm is fired immediately by the map.
        if (parsed.type === "panic_alert" && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rtg:panic_alert", { detail: parsed }));
        }
        // v2.6.9 batch CQ · public_booking → dispatch a custom DOM event so
        // the Admin Console can fire a toast + dispatch alarm sound the
        // instant a homepage Quick-Booking lands. Same pattern as panic_alert.
        if (parsed.type === "public_booking" && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rtg:public_booking", { detail: parsed }));
        }
        // v2.7.6 Audit · re-broadcast trip_completed as a window event so the
        // Revenue Today tile + compliance ledger can force-refresh the
        // instant a trip closes, instead of waiting for the 30 s poll.
        if (parsed.type === "trip_completed" && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rtg:trip_completed", { detail: parsed }));
        }
        // v2.7.7 · trip_initiated → re-broadcast for the Live Map counter badge + dispatch chime.
        if (parsed.type === "trip_initiated" && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rtg:trip_initiated", { detail: parsed }));
        }
        // v2.7.65+E146 · Phase 3.5 · New Trip upfront save → re-broadcast
        // so the Dispatch board can surface a "New Trip · Rider · Dest · $X"
        // toast the moment a driver hits Save in the New Trip form.
        if (parsed.type === "new_trip_created" && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rtg:new_trip_created", { detail: parsed }));
        }
        // v2.7.30 · Weather & Emergency Event Production — re-broadcast
        // NWS alerts + operator dispatch warnings so the dedicated
        // useWeatherStream hook can react (toast + alarm + UI refresh).
        if (parsed.type === "weather_alert" && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rtg:weather_alert", { detail: parsed }));
        }
        if (parsed.type === "manual_warning" && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rtg:manual_warning", { detail: parsed }));
        }
        // v2.6.9 batch BJ-4 · driver_dispatched → flash the marker for 1.5s.
        // Single state-set + setTimeout to remove from the Set is enough; the
        // map component reads `dispatchedRecently` and applies the pulse.
        if (parsed.type === "driver_dispatched" && parsed.driver_id) {
          const drvId = parsed.driver_id;
          setDispatchedRecently((prev) => new Set(prev).add(drvId));
          // v2.6.9 batch BJ-6 · persist last 10 dispatch events to localStorage
          // so the Overview tab's "Dispatch History" panel survives tab switches.
          try {
            const KEY = "rtg_dispatch_history";
            const prevHist = JSON.parse(localStorage.getItem(KEY) || "[]");
            const nextHist = [parsed, ...prevHist].slice(0, 10);
            localStorage.setItem(KEY, JSON.stringify(nextHist));
          } catch { /* localStorage may be unavailable in private mode */ }
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("rtg:driver_dispatched", { detail: parsed }));
          }
          setTimeout(() => {
            setDispatchedRecently((prev) => {
              if (!prev.has(drvId)) return prev;
              const next = new Set(prev); next.delete(drvId); return next;
            });
          }, 1500);
        }
        // v2.7.65+E86 · driver_online broadcast from backend `go_online`.
        // Adds the driver to the local map registry (if not already
        // present via a fresh ping) and fires a window event so the
        // audio engine plays driver-notification.mp3.
        if (parsed.type === "driver_online" && parsed.driver_id) {
          setDrivers((prev) => {
            if (prev[parsed.driver_id]) return prev;
            return { ...prev, [parsed.driver_id]: {
              driver_id: parsed.driver_id,
              name: parsed.driver_name,
              phone: parsed.phone,
              vehicle_model: parsed.vehicle_model,
              vehicle_make: parsed.vehicle_make,
              plate_number: parsed.plate_number,
              lat: null, lng: null,
              ts_iso: parsed.ts,
            }};
          });
          setOfflineDrivers((prev) => {
            if (!prev.has(parsed.driver_id)) return prev;
            const next = new Set(prev); next.delete(parsed.driver_id); return next;
          });
          if (typeof window !== "undefined") {
            try { window.dispatchEvent(new CustomEvent("rtg:driver_online", { detail: parsed })); } catch { /* noop */ }
          }
        }
      }
      // v2.7.65+E86 · driver-side: respond to targeted `ping_request`
      // from dispatch by firing a fresh high-accuracy GPS reading and
      // POSTing /drivers/location. Dedupe within 5s in case of retries.
      if (role === "driver" && parsed.type === "ping_request") {
        const now = Date.now();
        const last = lastPingRequestRef.current || 0;
        if (now - last < 5000) return;
        lastPingRequestRef.current = now;
        if (!navigator.geolocation) return;
        try {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { latitude, longitude, speed, heading } = pos.coords || {};
              // POST via same axios instance the driver Dashboard uses.
              try {
                import("../lib/api").then(({ default: api }) => {
                  api.post("/drivers/location", {
                    lat: latitude, lng: longitude,
                    speed: speed || 0, heading: heading || 0,
                    on_trip: false,
                    force_ping: true,
                  }).catch(() => { /* silent */ });
                });
              } catch { /* noop */ }
              // Also push through the open socket for immediate dispatch echo.
              try {
                wsRef.current?.send(JSON.stringify({
                  type: "ping",
                  lat: latitude, lng: longitude,
                  speed: speed || 0, heading: heading || 0,
                  t_emit: Date.now(),
                  forced: true,
                }));
              } catch { /* noop */ }
            },
            () => { /* permission denied — silent */ },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
          );
        } catch { /* Safari sync throw path */ }
      }
    };

    ws.onerror = () => { setStatus("error"); };

    ws.onclose = (ev) => {
      setStatus("closed");
      if (role === "driver") {
        stopGeoWatching();
        releaseWakeLock();
      }
      if (closedByUsRef.current) return;
      // 1008 = policy violation → don't auto-reconnect (e.g. no active trip for rider)
      if (ev.code === 1008) return;
      const n = ++reconnectAttemptsRef.current;
      const delay = Math.min(RECONNECT_BASE_MS * Math.pow(1.7, n - 1), RECONNECT_MAX_MS);
      setTimeout(() => {
        if (!closedByUsRef.current && active) connect();
      }, delay);
    };
  }, [role, token, active, acquireWakeLock, releaseWakeLock, startGeoWatching, stopGeoWatching]);

  useEffect(() => {
    if (!active || !token) return undefined;
    connect();
    return () => {
      closedByUsRef.current = true;
      try {
        wsRef.current?.close(1000, "client_unmount");
      } catch (e) {
        // Closing an already-closed socket can throw; non-fatal.
        console.warn("[useFleetStream] socket close failed:", e?.message || e);
      }
      if (role === "driver") {
        stopGeoWatching();
        releaseWakeLock();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, token]);

  return { status, events, drivers, lostDrivers, criticalDrivers, offlineDrivers, dispatchedRecently, pingLatencyMs, slowDrivers, driverLatencyMs };
}
